import { useSubjectLanguageVariants } from '@features/user';

import type { CalendarEvent } from '../../../types';

/**
 * Hook to get action message for actionable variant events
 *
 * Returns a user-facing message describing the action needed for this event.
 * Uses real API fields: status (AppointmentStatus) and confirmation_status.
 * Supports server-provided messages as future enhancement.
 *
 * @param event - Calendar event to get action message for
 * @returns Action message string, or undefined if no action is needed
 *
 * @example
 * const actionMessage = useActionMessage(event);
 * // Returns: "Suggested appointment time from recent encounter."
 * // Or: "Patient has requested to reschedule."
 * // Or: undefined (for standard appointments)
 */
export const useActionMessage = (event: CalendarEvent): string | undefined => {
  const reality = event.resource.block_state?.reality;

  // Get subject language for localized messaging (e.g., "Patient" vs "Client")
  const { singularCap: subjectLanguageCap, singular: subjectLanguage } =
    useSubjectLanguageVariants();

  if (reality === 'suggested') {
    return 'Suggested appointment time from recent encounter.';
  }
  if (reality === 'rescheduling') {
    return `${subjectLanguageCap} has requested to reschedule.`;
  }
  if (reality === 'cancelled') {
    return `This ${subjectLanguage} has canceled this appointment.`;
  }
  return undefined;
};
